import type { ConfirmationContentProps } from "../ClientTools";

import type { AddToPanelInput } from "./addToPanel";
import type { ApplyLayoutInput } from "./applyLayout";
import type { OpenFileInput } from "./openFile";
import type { OpenPanelInput } from "./openPanel";
import type { RemoveFromPanelInput } from "./removeFromPanel";
import type { RemovePanelInput } from "./removePanel";
import type { SearchTopicInput } from "./searchTopic";
import type { SeekInput } from "./seek";
import { VisualizeConfirmation } from "./VisualizeConfirmation";
import {
  messagePathBatchProblem,
  proposedString,
  proposedStringList,
  proposedTimestampNs,
} from "./visualizeProposal";

/**
 * Approval previews for the visualizer tools.
 *
 * Kept together rather than beside each tool because they are one design in
 * eight variations: each maps the tool's arguments onto the shared
 * {@link VisualizeConfirmation} shape and adds nothing else.
 */

export function OpenPanelConfirmation({
  params,
}: ConfirmationContentProps<OpenPanelInput>) {
  // `message_path_id` is the retired single-id form, still accepted by
  // `execute` so a call issued against the previous schema survives a deploy.
  // The preview has to read it too, or those calls show as empty.
  const single = proposedString(params.message_path_id);
  const messagePathIds =
    proposedStringList(params.message_path_ids) ??
    (single === undefined ? undefined : [single]);
  const panelType = proposedString(params.panel_type);

  return (
    <VisualizeConfirmation
      proposal={{
        title:
          panelType === undefined
            ? "Open a panel"
            : `Open a ${panelType} panel`,
        label: "Open panel preview",
        messagePathIds,
        timestampNs: proposedTimestampNs(params.timestamp),
        problem: messagePathBatchProblem(messagePathIds),
      }}
    />
  );
}

export function AddToPanelConfirmation({
  params,
}: ConfirmationContentProps<AddToPanelInput>) {
  const panelId = proposedString(params.panel_id);
  const messagePathIds = proposedStringList(params.message_path_ids);

  return (
    <VisualizeConfirmation
      proposal={{
        title: "Add signals to a panel",
        label: "Add to panel preview",
        panelId,
        messagePathIds,
        problem:
          panelId === undefined
            ? "No panel was named."
            : messagePathBatchProblem(messagePathIds),
      }}
    />
  );
}

export function RemoveFromPanelConfirmation({
  params,
}: ConfirmationContentProps<RemoveFromPanelInput>) {
  const panelId = proposedString(params.panel_id);
  const messagePathIds = proposedStringList(params.message_path_ids);

  return (
    <VisualizeConfirmation
      proposal={{
        title: "Remove signals from a panel",
        label: "Remove from panel preview",
        panelId,
        messagePathIds,
        problem:
          panelId === undefined
            ? "No panel was named."
            : messagePathBatchProblem(messagePathIds),
      }}
    />
  );
}

export function RemovePanelConfirmation({
  params,
}: ConfirmationContentProps<RemovePanelInput>) {
  const panelId = proposedString(params.panel_id);

  return (
    <VisualizeConfirmation
      proposal={{
        title: "Close a panel",
        label: "Close panel preview",
        panelId,
        problem: panelId === undefined ? "No panel was named." : undefined,
      }}
    />
  );
}

export function SearchTopicConfirmation({
  params,
}: ConfirmationContentProps<SearchTopicInput>) {
  const topicId = proposedString(params.topic_id);

  return (
    <VisualizeConfirmation
      proposal={{
        title: "Open the visualizer filtered to a topic",
        label: "Search topic preview",
        topicId,
        timestampNs: proposedTimestampNs(params.timestamp),
        problem: topicId === undefined ? "No topic was named." : undefined,
      }}
    />
  );
}

export function OpenFileConfirmation({
  params,
}: ConfirmationContentProps<OpenFileInput>) {
  const fileId = proposedString(params.file_id);

  return (
    <VisualizeConfirmation
      proposal={{
        title: "Open a file",
        label: "Open file preview",
        fileId,
        timestampNs: proposedTimestampNs(params.timestamp),
        problem: fileId === undefined ? "No file was named." : undefined,
      }}
    />
  );
}

export function SeekConfirmation({
  params,
}: ConfirmationContentProps<SeekInput>) {
  const timestampNs = proposedTimestampNs(params.timestamp);

  return (
    <VisualizeConfirmation
      proposal={{
        title: "Move the timeline",
        label: "Seek preview",
        timestampNs,
        problem:
          timestampNs === undefined ? "No timestamp was given." : undefined,
      }}
    />
  );
}

export function ApplyLayoutConfirmation({
  params,
}: ConfirmationContentProps<ApplyLayoutInput>) {
  const layoutId = proposedString(params.layout_id);

  return (
    <VisualizeConfirmation
      proposal={{
        title: "Apply a saved layout",
        label: "Apply layout preview",
        layoutId,
        problem: layoutId === undefined ? "No layout was named." : undefined,
      }}
    />
  );
}
